// engineClient — promise-based RPC client over the engine Web Worker. The UI thread calls
// `client.call('score', {...})` and awaits the typed result. When no Worker is available (unit
// tests, SSR), it transparently falls back to the SAME pure dispatcher the worker runs, so behaviour
// is identical (structural guarantee, spec §6: Studio == online via one engine).
import {
  runEngineRequest,
  type EngineRequest,
  type EngineResponse,
  type EngineResultMap,
} from './engineRpc';

// The request fields minus the bookkeeping `id` (the client assigns ids).
type RequestArgs<M extends EngineRequest['method']> = Omit<Extract<EngineRequest, { method: M }>, 'id' | 'method'>;

export interface EngineClient {
  call<M extends EngineRequest['method']>(method: M, args: RequestArgs<M>): Promise<EngineResultMap[M]>;
  dispose(): void;
}

/** Worker-backed client (browser). */
export function createWorkerEngineClient(worker: Worker): EngineClient {
  let nextId = 1;
  const pending = new Map<number, { resolve: (v: unknown) => void; reject: (e: Error) => void }>();

  worker.onmessage = (ev: MessageEvent<EngineResponse>) => {
    const res = ev.data;
    const p = pending.get(res.id);
    if (!p) return;
    pending.delete(res.id);
    if (res.ok) p.resolve(res.result);
    else p.reject(new Error(res.error));
  };

  return {
    call(method, args) {
      const id = nextId++;
      const req = { id, method, ...args } as unknown as EngineRequest;
      return new Promise((resolve, reject) => {
        pending.set(id, { resolve: resolve as (v: unknown) => void, reject });
        worker.postMessage(req);
      });
    },
    dispose() {
      worker.terminate();
      pending.clear();
    },
  };
}

/** In-process client (tests / fallback). Runs the dispatcher synchronously but presents the async API. */
export function createLocalEngineClient(): EngineClient {
  let nextId = 1;
  return {
    call(method, args) {
      const id = nextId++;
      const req = { id, method, ...args } as unknown as EngineRequest;
      const res = runEngineRequest(req);
      if (res.ok) return Promise.resolve(res.result as never);
      return Promise.reject(new Error(res.error));
    },
    dispose() {
      /* nothing to clean up */
    },
  };
}

/**
 * Construct the default client: a real Worker in the browser, the local dispatcher elsewhere.
 * The worker module URL uses `import.meta.url` so Vite bundles `engine.worker.ts` as a worker chunk.
 */
export function createDefaultEngineClient(): EngineClient {
  if (typeof Worker !== 'undefined' && typeof window !== 'undefined') {
    const worker = new Worker(new URL('./engine.worker.ts', import.meta.url), { type: 'module' });
    return createWorkerEngineClient(worker);
  }
  return createLocalEngineClient();
}
